import type { CSSObject } from '../theme/themeTypes'
import type { Theme } from '../theme'
import { serializeCss } from '../../utils/css'
import { styled } from '../../utils/styled'

/** `dataViz.primary` palette, named like the matching `Badge` colours. */
const INLINE_CHIP_COLOR_INDEX = {
  'precis-blue': 1,
  teal: 2,
  orange: 3,
  magenta: 4,
  violet: 5,
  andesite: 6,
  cobalt: 7,
  brown: 8
} as const

export type InlineChipColor = keyof typeof INLINE_CHIP_COLOR_INDEX

export const INLINE_CHIP_COLORS = Object.keys(INLINE_CHIP_COLOR_INDEX) as InlineChipColor[]

/** Colours for inline chips — shared by `InlineChip` and composer chips. */
export const getInlineChipColorStyles = (theme: Theme, color: InlineChipColor = 'precis-blue'): CSSObject => {
  const palette = theme.tokens.dataViz.primary[INLINE_CHIP_COLOR_INDEX[color]]
  return {
    color: palette.fill.default,
    backgroundColor: palette.fill.tonal.superSoft.default,
    border: `1px solid ${palette.border.subtle}`
  }
}

export const getInlineChipLayoutStyles = (theme: Theme): CSSObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  columnGap: theme.spacing(0),
  paddingBlock: theme.spacing(0),
  paddingInline: theme.spacing(1),
  marginInlineEnd: theme.spacing(0),
  borderRadius: theme.borderRadius.xSmall,
  fontSize: '0.8rem',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  verticalAlign: 'baseline',
  userSelect: 'none'
})

/**
 * Inline `style` text for chips rendered as raw HTML (composer contenteditable),
 * built from the same styles as `InlineChip` so both look identical.
 */
export const getInlineChipCssText = (theme: Theme, color?: InlineChipColor): string =>
  serializeCss({ ...getInlineChipLayoutStyles(theme), ...getInlineChipColorStyles(theme, color) })

export const StyledInlineChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'chipColor'
})<{ chipColor: InlineChipColor }>(({ theme, chipColor }) => ({
  ...getInlineChipLayoutStyles(theme),
  ...getInlineChipColorStyles(theme, chipColor),
  verticalAlign: 'middle'
}))
